/* eslint-disable @typescript-eslint/no-explicit-any */
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useAlert } from "../../../../../../lib/alert";
import { addressToString } from "../../../../../../model/Address";
import {
  FieldDefinitionFormElement,
  FormElementType,
  HeadingFormElement,
  TaskFormExecution,
} from "../../../../types";
import type { JSX } from "react";

export const TaskSummaryFormComponent = ({
  ...props
}: {
  disabled: boolean;
  execution: TaskFormExecution;
  onGoToStep?: (stepNumber: number) => void;
}) => {
  const alert = useAlert();
  const elements = props.execution.data?.form?.elements || [];
  const validIndexes =
    props.execution.data?.wizardData?.validationIndexes || [];

  if (!elements || !validIndexes) {
    alert.error("Error loading elements for summmary.");
    return <></>;
  }

  const summaryElements: JSX.Element[] = [];
  const unableToRender: string[] = [];
  const missingRequired: string[] = [];
  let currentStepInSummary = -1;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  elements.forEach((e, i) => {
    // Helper to check missing required value
    const isMissing = (val: any) => {
      if (!e.required) return false;
      if (val === undefined || val === null) return true;
      if (typeof val === "string" && val.trim() === "") return true;
      if (Array.isArray(val) && val.length === 0) return true;
      return false;
    };

    const shouldAddMissing = (isMissingValue: boolean) =>
      isMissingValue && validIndexes.includes(i);

    // Determine if this step is completed and fails validation
    let stepFailedValidation = false;
    if (e.type === FormElementType.Heading) {
      // A step is considered completed if its index is in validIndexes
      // We'll check if any required element in this step is missing
      if (validIndexes.includes(i)) {
        // Find all elements in this step (from this heading to next heading or end)
        const startIdx = i;
        let endIdx = elements.length;
        for (let j = i + 1; j < elements.length; j++) {
          if (elements[j].type === FormElementType.Heading) {
            endIdx = j;
            break;
          }
        }
        for (let k = startIdx + 1; k < endIdx; k++) {
          const el = elements[k];
          // Check direct required fields
          if (el.required) {
            const val = (el as any).value;
            if (
              val === undefined ||
              val === null ||
              (typeof val === "string" && val.trim() === "") ||
              (Array.isArray(val) && val.length === 0)
            ) {
              stepFailedValidation = true;
              break;
            }
          }
          // Check required fields inside FieldDefinition
          if (el.type === FormElementType.FieldDefinition) {
            const fieldDef = el as FieldDefinitionFormElement;
            if (Array.isArray(fieldDef.value)) {
              for (const field of fieldDef.value) {
                if (field.required) {
                  const fieldVal = (field as any).value;
                  if (
                    fieldVal === undefined ||
                    fieldVal === null ||
                    (typeof fieldVal === "string" && fieldVal.trim() === "") ||
                    (Array.isArray(fieldVal) && fieldVal.length === 0)
                  ) {
                    stepFailedValidation = true;
                    break;
                  }
                }
              }
              if (stepFailedValidation) break;
            }
          }
        }
      }
    }
    switch (e.type) {
      /*
       * Unsupported/Ignored Types
       */
      case FormElementType.NestedForm:
      case FormElementType.Typography:
      case FormElementType.ButtonGroup:
        return;

      /*
       * Other Types
       */
      case FormElementType.Address:
        if (shouldAddMissing(isMissing(e.value))) missingRequired.push(e.label);
        summaryElements.push(
          <>
            <Typography sx={{ ml: 1 }}>
              {`${e.label}: ${addressToString(e.value)}`}
            </Typography>
          </>,
        );
        return;
      case FormElementType.Heading:
        currentStepInSummary++;
        if (!(e as HeadingFormElement).hideInSummary) {
          const stepToGoTo = currentStepInSummary;
          const stepWasNotSkipped = validIndexes.includes(i);
          summaryElements.push(
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                mt: i == 0 ? 0 : 1,
                mb: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: stepFailedValidation
                    ? (t) => t.palette.warning.main
                    : stepWasNotSkipped
                      ? "primary.main"
                      : "inherit",
                  cursor: "pointer",
                  fontWeight: 700,
                  textDecoration: "underline",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": stepWasNotSkipped
                    ? {
                        color: stepFailedValidation
                          ? (t) => t.palette.warning.dark
                          : stepWasNotSkipped
                            ? "primary.dark"
                            : "inherit",
                      }
                    : {},
                  transition: "color 0.2s",
                  mr: stepWasNotSkipped ? 0 : 1,
                }}
                onClick={() => {
                  props.onGoToStep?.(stepToGoTo);
                }}
              >
                {e.label + " \u203A"}
                {stepFailedValidation && (
                  <WarningAmberIcon
                    sx={{
                      ml: 1,
                      color: (t) => t.palette.warning.main,
                      fontSize: "1.2em",
                    }}
                  />
                )}
              </Box>
              {!stepWasNotSkipped && (
                <Box
                  sx={(t) => ({
                    background: t.palette.primary.dark,
                    color: t.palette.primary.contrastText,
                    px: 0.7,
                    borderRadius: 2,
                  })}
                >
                  <Typography fontSize={"0.8em"}>{"NONE"}</Typography>
                </Box>
              )}
            </Typography>,
          );
        }
        return;
      case FormElementType.FieldDefinition:
        (e as FieldDefinitionFormElement).value?.forEach((field) => {
          if (
            field.required &&
            shouldAddMissing(isMissing((field as any).value))
          )
            missingRequired.push(field.label);
          summaryElements.push(
            <Typography sx={{ ml: 1 }}>
              {field.label}: {(field as any).value}
            </Typography>,
          );
        });
        return;
      case FormElementType.Date:
        if (shouldAddMissing(isMissing(e.value))) missingRequired.push(e.label);
        if (e.value?.length && e.value.length > 1)
          summaryElements.push(
            <>
              <Typography sx={{ ml: 1 }}>{`${e.label}: `}</Typography>
              {e.value.map((date, i) => (
                <Typography key={i} sx={{ ml: 2 }}>
                  {`- ${new Date(date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}`}
                </Typography>
              ))}
            </>,
          );
        else if (e.value?.length)
          summaryElements.push(
            <Typography sx={{ ml: 1 }}>
              {`${e.label}: ${new Date(e.value[0]).toLocaleDateString(
                undefined,
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )}`}
            </Typography>,
          );
        return;
      case FormElementType.Radio:
        if (shouldAddMissing(isMissing(e.value?.name)))
          missingRequired.push(e.label);
        summaryElements.push(
          <Typography
            sx={{ ml: 1 }}
          >{`${e.label}: ${e.value?.name}`}</Typography>,
        );
        return;
      case FormElementType.MultiSelection:
        if (shouldAddMissing(isMissing(e.value))) missingRequired.push(e.label);
        summaryElements.push(
          <>
            <Typography sx={{ ml: 1 }}>{`${e.label}:`}</Typography>
            {Array.isArray(e.value) &&
              e.value.length > 0 &&
              e.value.map((item: any, idx: number) => (
                <Typography key={idx} sx={{ ml: 2 }}>
                  {`- ${item.name}`}
                </Typography>
              ))}
          </>,
        );
        return;
      default:
        // eslint-disable-next-line no-case-declarations
        const val = (e as any).value;
        if (shouldAddMissing(isMissing(val))) missingRequired.push(e.label);
        if (typeof val !== "string" && val !== undefined) {
          unableToRender.push(e.label);
        }
        if (e.label)
          summaryElements.push(
            <Typography sx={{ ml: 1 }}>
              {e.label}: {val ? val.toString() : ""}
            </Typography>,
          );
    }
  });

  if (unableToRender.length > 0) {
    console.warn(
      `Unable to map elements in summary: ${unableToRender.join(", ")}`,
    );
  }

  return (
    <>
      <Box
        sx={(t) => ({
          p: 3,
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 2,
          background: t.palette.background.paper,
          maxHeight: "35vh",
          overflow: "scroll",
          margin: "0 auto",
        })}
      >
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {summaryElements.map((el, idx) => (
            <Box component="li" key={idx} sx={{ mb: 0 }}>
              {el}
            </Box>
          ))}
        </Box>
      </Box>

      {missingRequired.length > 0 && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Alert severity="warning">
            <Typography variant="body2">
              <b>{"You currently have some incomplete field(s): "}</b>
              {missingRequired.join(", ")}
            </Typography>
          </Alert>
        </Box>
      )}
    </>
  );
};
